/**
 * SAM파일 노드 속성 — 데이터 정의 · 파일 · 형식 · 항목.
 *
 * 예전에는 JSON 스키마 폼(RJSF)으로 그려서 DB테이블·데이터처리 패널과 모양이 달랐고, 칸이 세로로 늘어서기만 했다.
 * 아래에는 DB 노드용 '연결 정보(No connection selected.)' 칸까지 따라 나왔다. 현장 요청(2026-09-22)으로
 * DB테이블 패널(RdbDataPanel)과 같은 부품(ui.tsx)·같은 묶음 순서로 옮겼다.
 *
 *  - 형식: 구분자(가변폭)/고정폭 중 고른 쪽의 칸만 보인다. 항목명은 고정폭이거나 첫 줄이 헤더가 아닐 때만 쓰인다
 *  - 항목: 몇 개인지와 언제 읽었는지. '갱신' 은 파일 첫 줄(또는 항목명)에서 항목을 다시 읽는다(구분자 파일만).
 *    상세보기는 DB테이블·데이터처리와 같은 팝업(항목 보기 / 값 보기)
 *  - 데이터 정의를 고르면 파일·형식·항목은 정의에서 읽고(노드에는 정의 id 만 저장 — utils/definitionRefs) 그 칸들은 읽기 전용
 *  - 저장하는 값의 이름·모양은 예전과 같다 — 변환기(canvas_converter)가 그대로 읽는다
 */
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { AppDispatch, RootState } from '../../../../store';
import { updateNodeData } from '../../../../store/workflow/workflowSlice';
import { be1Fetch } from '../../../../config/api';
import { errorText } from '../../../../services/errorText';
import { nodeColumns, Column } from '../../utils/transformLoad';
import { diffColumns, hasChanges } from '../../utils/columnsDiff';
import ServerFileBrowserDialog from '../DataDefinition/ServerFileBrowserDialog';
import DataDetailModal from './DataDetailModal';
import DefinitionLinkSection from './DefinitionLinkSection';
import { linkedDefinition } from '../../utils/definitionRefs';
import { FieldLabel, PanelHeader, Section, formatFetchedAt, inputCls, selectCls, smallBtn } from './ui';

interface Props {
  node: any;
  /** 최근 실행의 입력·출력 데이터 열기 — 상위 패널이 캔버스 변환까지 맡는다 */
  onOpenNodeData: () => void;
  nodeDataBusy: boolean;
}

const ENCODINGS = ['UTF-8', 'UTF-8-SIG', 'CP949', 'EUC-KR', 'UTF-16', 'LATIN-1'];

const SamFilePanel: React.FC<Props> = ({ node, onOpenNodeData, nodeDataBusy }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const cfg = useMemo(() => node?.data?.config || {}, [node]);
  const filepath: string = cfg.filepath || '';
  const format: 'delimited' | 'fixed' = cfg.format === 'fixed' ? 'fixed' : 'delimited';
  const hasHeader = cfg.hasHeader !== false;
  const encoding: string = cfg.encoding || 'UTF-8';
  const fixed = format === 'fixed';
  // 데이터 정의를 가리키면 파일·형식·항목은 정의에서 읽는다 — 이 칸들은 읽기 전용 (utils/definitionRefs)
  const defs = useSelector((s: RootState) => s.dataDefinitions.items);
  const linked = !!linkedDefinition(node, defs);
  const ro = linked ? { disabled: true, title: t('property:deflink.fromDef') } : {};

  const patch = useCallback((p: Record<string, any>) => {
    const next = { ...cfg, ...p };
    dispatch(updateNodeData({ id: node.id, data: { config: next, ...(p.label !== undefined ? { label: p.label } : {}) } } as any));
  }, [cfg, dispatch, node.id]);

  const columns: Column[] = useMemo(() => nodeColumns(node), [node]);
  const [colState, setColState] = useState<{ loading?: boolean; error?: string }>({});
  const [browseOpen, setBrowseOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  /** 파일 첫 줄(헤더) 또는 항목명에서 항목을 다시 읽는다. 자료형은 이름이 같으면 예전 것을 살린다 */
  const refreshColumns = async () => {
    if (!filepath || fixed) return;
    setColState({ loading: true });
    try {
      const res = await be1Fetch('/api/data/preview', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: 'csv', filepath, delimiter: cfg.delimiter || ',', hasHeader, fieldNames: cfg.fieldNames || undefined, encoding, limit: 1 }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(errorText(body, res.statusText));
      const old = new Map(columns.map((c) => [c.name, c]));
      const next: Column[] = (body.columns || []).map((name: string) => ({ name, dataType: old.get(name)?.dataType || '', comment: old.get(name)?.comment || '' }));
      const at = new Date().toISOString();
      const p: Record<string, any> = { tableColumns: next, columnsFetchedAt: at };
      const d = diffColumns(columns, next);
      // 바뀐 게 있을 때만 새로 적는다 — 없으면 예전 기록을 그대로 둔다(아직 확인하지 않은 변경일 수 있다)
      if (columns.length && hasChanges(d)) p.columnsChange = { at, ...d };
      patch(p);
      setColState({});
    } catch (e: any) {
      setColState({ error: t('property:sam.columnsFailed', { message: e?.message || '' }) });
    }
  };

  const missing = filepath ? [] : [t('workflow:data.filePath')];
  const radio = (value: 'delimited' | 'fixed', label: string) => (
    <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
      <input type="radio" name={`sam-format-${node.id}`} disabled={linked} checked={format === value} onChange={() => patch({ format: value })} />
      {label}
    </label>
  );

  return (
    <div className="h-full bg-white p-4 overflow-auto">
      <PanelHeader typeLabel={t('property:samf.typeLabel')} right={
        <button type="button" className={smallBtn} disabled={nodeDataBusy} onClick={onOpenNodeData}
          title={t('property:rdb.nodeDataTitle')}>
          {nodeDataBusy ? t('property:rdb.opening') : t('property:rdb.nodeData')}
        </button>
      } />

      <div className="mb-3">
        <FieldLabel>{t('workflow:field.label')}</FieldLabel>
        <input className={inputCls} value={cfg.label ?? node.data?.label ?? ''} onChange={(e) => patch({ label: e.target.value })} placeholder={t('property:rdb.labelPlaceholder')} />
      </div>

      {missing.length > 0 && (
        <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
          {t('property:rdb.missing', { fields: missing.join(', ') })}
        </div>
      )}

      <DefinitionLinkSection node={node} defType="sam" patch={patch} />

      <Section title={t('workflow:field.filePath')} required>
        <div className="flex gap-1">
          <input className={inputCls} aria-label={t('workflow:field.filePath')} {...ro} value={filepath} placeholder={t('workflow:schema.filePathPlaceholder')}
            onChange={(e) => patch({ filepath: e.target.value || undefined })} />
          <button type="button" className={smallBtn} disabled={linked} onClick={() => setBrowseOpen(true)} title={t('property:fp.browseTitle')}>{t('property:rdb.browse')}</button>
        </div>
      </Section>

      <Section title={t('property:samf.format')}>
        <div className="flex items-center gap-4 mb-2">
          {radio('delimited', t('property:samf.delimited'))}
          {radio('fixed', t('property:samf.fixed'))}
        </div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          {fixed ? (
            <div className="col-span-2">
              <FieldLabel>{t('workflow:schema.fieldWidths')}</FieldLabel>
              <input className={inputCls} {...ro} value={cfg.fieldWidths ?? ''} placeholder={t('workflow:schema.fieldWidthsPlaceholder')}
                onChange={(e) => patch({ fieldWidths: e.target.value || undefined })} />
            </div>
          ) : (
            <div>
              <FieldLabel>{t('workflow:field.delimiter')}</FieldLabel>
              <input className={`${inputCls} font-mono`} {...ro} value={cfg.delimiter ?? ','} placeholder={t('workflow:field.delimiterPlaceholder')}
                onChange={(e) => patch({ delimiter: e.target.value })} />
            </div>
          )}
          <div className={fixed ? 'col-span-2' : ''}>
            <FieldLabel>{t('workflow:field.encoding')}</FieldLabel>
            <select className={selectCls} {...ro} value={encoding} onChange={(e) => patch({ encoding: e.target.value })}>
              {!ENCODINGS.includes(encoding) && <option value={encoding}>{encoding}</option>}
              {ENCODINGS.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer mb-2">
          <input type="checkbox" disabled={linked} checked={hasHeader} onChange={(e) => patch({ hasHeader: e.target.checked })} />
          {t('workflow:field.hasHeader')}
        </label>
        {/* 항목명은 첫 줄에서 이름을 얻지 못할 때 쓴다 — 고정폭이거나 헤더가 없는 파일 */}
        {(fixed || !hasHeader) && (
          <>
            <FieldLabel>{t('property:samf.fieldNames')}</FieldLabel>
            <input className={inputCls} {...ro} value={cfg.fieldNames ?? ''} placeholder={t('workflow:schema.fieldNamesPlaceholder')}
              onChange={(e) => patch({ fieldNames: e.target.value || undefined })} />
          </>
        )}
      </Section>

      <Section title={t('property:samf.columns')} right={
        <>
          <button type="button" className={smallBtn} disabled={!filepath || fixed || linked || colState.loading} onClick={refreshColumns}
            title={linked ? t('property:deflink.fromDef') : fixed ? t('property:samf.fixedNoRefresh') : t('property:samf.refreshTitle')}>{t('property:rdb.refresh')}</button>
          <button type="button" className={smallBtn} disabled={!filepath} onClick={() => setDetailOpen(true)}>{t('property:rdb.detail')}</button>
        </>
      }>
        {!filepath ? <div className="text-xs text-gray-400">{t('property:samf.pickFileFirst')}</div> : (
          <div className="text-xs text-gray-600 space-y-0.5">
            <div>
              {colState.loading ? t('property:rdb.reading') : colState.error ? <span className="text-red-600">{colState.error}</span>
                : columns.length ? t('property:rdb.columnCount', { n: columns.length }) : <span className="text-gray-400">{t('property:samf.noColumns')}</span>}
              {cfg.columnsFetchedAt && !colState.loading && <span className="text-gray-400">{t('property:rdb.fetchedAt', { at: formatFetchedAt(cfg.columnsFetchedAt) })}</span>}
            </div>
            {cfg.columnsChange && (
              <div className="rounded bg-amber-50 border border-amber-200 px-1.5 py-1 text-[0.6875rem] text-amber-800">
                {t('property:rdb.changedAt', { at: formatFetchedAt(cfg.columnsChange.at) })}
                {cfg.columnsChange.added?.length ? t('property:rdb.added', { cols: cfg.columnsChange.added.join(', ') }) : ''}
                {cfg.columnsChange.removed?.length ? t('property:rdb.removed', { cols: cfg.columnsChange.removed.join(', ') }) : ''}
                {cfg.columnsChange.typeChanged?.length ? t('property:rdb.typeChanged', { cols: cfg.columnsChange.typeChanged.join(', ') }) : ''}
                <div className="text-amber-700/80">{t('property:samf.changeNote')}</div>
              </div>
            )}
          </div>
        )}
      </Section>

      {browseOpen && (
        <ServerFileBrowserDialog
          onSelect={(relPath) => { patch({ filepath: relPath }); setBrowseOpen(false); }}
          onClose={() => setBrowseOpen(false)}
        />
      )}
      {detailOpen && (
        <DataDetailModal
          title={t('property:samf.detailTitle')}
          subtitle={filepath}
          columns={columns}
          loading={colState.loading}
          error={colState.error}
          // 값 보기는 구분자 파일만 — 미리보기 API 가 고정폭을 자르지 못한다
          preview={fixed ? null : { kind: 'csv', filepath, delimiter: cfg.delimiter || ',', hasHeader, fieldNames: cfg.fieldNames || undefined, encoding }}
          onClose={() => setDetailOpen(false)}
        />
      )}
    </div>
  );
};

export default SamFilePanel;
